import { useState, useEffect } from "react";
import { apiClient, usePermissions } from "../../App";
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle,
  ChevronRight,
  Calendar,
  Clock,
  CheckCircle2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";
import { Progress } from "../ui/progress";

export default function FinancialWidget() {
  const { canViewFinancials } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (canViewFinancials) {
      fetchFinancialData();
    } else {
      setLoading(false);
    }
  }, [canViewFinancials]);

  const fetchFinancialData = async () => {
    try {
      // Get receivables for this month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      
      const [receivablesRes, summaryRes] = await Promise.all([
        apiClient.get(`/receivables?due_start=${startOfMonth}&due_end=${endOfMonth}`).catch(() => ({ data: [] })),
        apiClient.get('/receivables/summary').catch(() => ({ data: {} }))
      ]);

      const receivables = receivablesRes.data || [];
      const summary = summaryRes.data || {};

      // Calculate this month's metrics
      const expectedThisMonth = receivables.reduce((sum, r) => sum + (r.expected_amount || 0), 0);
      const collectedThisMonth = receivables.reduce((sum, r) => sum + (r.collected_amount || 0), 0);
      const pendingThisMonth = expectedThisMonth - collectedThisMonth;
      
      // Get overdue count
      const overdueCount = receivables.filter(r => r.status === 'overdue').length;
      const upcomingPayments = receivables
        .filter(r => r.status !== 'paid' && r.status !== 'overdue')
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
        .slice(0, 3);

      setData({
        expectedThisMonth,
        collectedThisMonth,
        pendingThisMonth,
        overdueCount,
        pastDueTotal: summary.past_due_total || 0,
        upcomingPayments,
        collectionRate: expectedThisMonth > 0 ? Math.round((collectedThisMonth / expectedThisMonth) * 100) : 0,
        monthlyOutlook: summary.monthly_outlook || []
      });
    } catch (err) {
      console.error("Failed to fetch financial data", err);
    }
    setLoading(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return `${Math.abs(diff)}d overdue`;
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return `${diff}d`;
  };

  // Don't render if user can't view financials
  if (!canViewFinancials) {
    return null;
  }

  if (loading) {
    return (
      <Card data-testid="financial-widget">
        <CardContent className="py-8 text-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <Card data-testid="financial-widget" className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="pr-0.5">
              <DollarSign className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
            </div>
            <div>
              <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-emerald-800">
                Financial Widget
              </CardTitle>
              <p className="text-[10px] text-emerald-600">{currentMonth}</p>
            </div>
          </div>
          <Link 
            to="/money" 
            className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1 transition-colors"
          >
            View All <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Main Metrics */}
        <div className="grid grid-cols-3 hairline-divide">
          <div className="pr-4">
            <p className="micro-label mb-1.5">Expected</p>
            <p className="text-2xl tabular-nums text-foreground">
              {formatCurrency(data.expectedThisMonth)}
            </p>
          </div>
          <div className="px-4">
            <p className="micro-label mb-1.5">Collected</p>
            <p className="text-2xl tabular-nums text-green-600">
              {formatCurrency(data.collectedThisMonth)}
            </p>
          </div>
          <div className="px-4">
            <p className="micro-label mb-1.5">Pending</p>
            <p className="text-2xl tabular-nums text-muted-foreground">
              {formatCurrency(data.pendingThisMonth)}
            </p>
          </div>
        </div>

        {/* Collection Progress */}
        <div>
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-muted-foreground">Collection Progress</span>
            <span className="font-medium text-emerald-700">{data.collectionRate}%</span>
          </div>
          <Progress value={data.collectionRate} />
        </div>

        {/* Alerts Row */}
        {(data.overdueCount > 0 || data.pastDueTotal > 0) && (
          <div className="flex items-center gap-2.5 py-3 border-t border-terracotta-200">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-red-700">
                {data.overdueCount} payment{data.overdueCount !== 1 ? 's' : ''} overdue
              </p>
              {data.pastDueTotal > 0 && (
                <p className="text-[10px] text-red-600">
                  {formatCurrency(data.pastDueTotal)} past due
                </p>
              )}
            </div>
            <Link 
              to="/money?filter=overdue" 
              className="text-xs text-red-600 hover:text-red-800 font-medium"
            >
              View
            </Link>
          </div>
        )}

        {/* Upcoming Payments */}
        {data.upcomingPayments && data.upcomingPayments.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Clock className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-xs font-medium text-muted-foreground">Upcoming</span>
            </div>
            <div>
              {data.upcomingPayments.map(payment => (
                <Link
                  key={payment.id}
                  to={`/programs/${payment.program_id}`}
                  className="flex items-center justify-between py-2.5 px-1 border-t border-border first:border-t-0 text-sm hover:bg-muted/60 transition-colors duration-250 ease-brand group"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate group-hover:text-emerald-700 transition-colors">
                      {payment.description}
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {payment.client_name || payment.program_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-700">
                        {formatCurrency(payment.expected_amount)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {getDaysUntil(payment.due_date)}
                      </p>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-emerald-600" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
